import type {
  BodyBlock,
  Group,
  GroupKind,
  PreambleNode,
  Statute,
  Unit,
  UnitKind,
} from '../types';

/**
 * v2 family-suits adapter (Family Courts Act 1964, Muslim Family Laws
 * Ordinance 1961). Same v2 envelope as civils-suits, but:
 *  - top-level structure is typically a flat list of `section` nodes (no
 *    `preliminary` / `part` containers).
 *  - subsection labels are sometimes `null`.
 *  - schedules introduce a `scheduleItem` leaf type with title-only payload.
 *  - sections often carry `sectionNo` (numeric label without "Section " prefix).
 */

type AnyRecord = Record<string, unknown>;

const isObject = (v: unknown): v is AnyRecord =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const trimOrEmpty = (v: unknown): string => asString(v).trim();

const CONTAINER_TYPES = new Set([
  'preamble',
  'preliminary',
  'part',
  'chapter',
  'heading',
  'schedule',
  'appendix',
  'order',
  'division',
]);

const LEAF_TYPES = new Set(['section', 'article', 'rule', 'form', 'scheduleItem']);

function devWarn(message: string, ...rest: unknown[]) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[v2FamilySuitsAdapter] ${message}`, ...rest);
  }
}

function pickGroupKind(type: string): GroupKind {
  switch (type) {
    case 'preliminary':
      return 'preliminary';
    case 'part':
      return 'part';
    case 'chapter':
      return 'chapter';
    case 'order':
      return 'order';
    case 'schedule':
      return 'schedule';
    case 'appendix':
      return 'appendix';
    case 'division':
      return 'division';
    default:
      return 'other';
  }
}

function pickUnitKind(type: string): UnitKind {
  switch (type) {
    case 'article':
      return 'article';
    case 'rule':
      return 'rule';
    case 'form':
      return 'form';
    case 'scheduleItem':
    case 'section':
    default:
      return 'section';
  }
}

function unitNumberFromNode(node: AnyRecord, fallback: string): string {
  const sectionNo = trimOrEmpty(node.sectionNo);
  if (sectionNo) return sectionNo;
  const articleNo = trimOrEmpty(node.articleNo);
  if (articleNo) return articleNo;
  const ruleNo = trimOrEmpty(node.ruleNo);
  if (ruleNo) return ruleNo;
  const label = trimOrEmpty(node.label);
  if (label) {
    let m = label.match(/^Section\s+([A-Za-z0-9]+)$/i);
    if (m) return m[1];
    m = label.match(/^Article\s+([A-Za-z0-9]+)$/i);
    if (m) return m[1];
    m = label.match(/^Rule\s+([A-Za-z0-9]+)$/i);
    if (m) return m[1];
    m = label.match(/^Form\s+([A-Za-z0-9]+)$/i);
    if (m) return m[1];
    m = label.match(/^([A-Za-z0-9]+)$/);
    if (m) return m[1];
    m = label.match(/([A-Za-z0-9]+)\s*$/);
    if (m) return m[1];
  }
  // Fall back to id suffix.
  const id = trimOrEmpty(node.id);
  const idMatch = id.match(/(?:^|-)([A-Za-z0-9]+)$/);
  if (idMatch) return idMatch[1];
  return fallback;
}

function unitNumberDisplay(kind: UnitKind, number: string, label: string): string {
  if (label && label.trim()) return label.trim();
  switch (kind) {
    case 'article':
      return `Article ${number}`;
    case 'rule':
      return `Rule ${number}`;
    case 'form':
      return `Form ${number}`;
    default:
      return `Section ${number}`;
  }
}

function stripParenLabel(label: string): string {
  const t = label.trim();
  const m = t.match(/^\(([^)]+)\)$/);
  return m ? m[1] : t;
}

const normalizeTextForCompare = (s: string): string => s.replace(/\s+/g, ' ').trim().toLowerCase();

function stripFootnoteBlock(text: string): string {
  if (!text) return text;
  // Strip trailing footnote definition trailer that starts after a long divider.
  return text.replace(/[—_\-]{5,}[\s\S]*$/m, '').trim();
}

function cleanAmendmentMarkers(text: string): string {
  if (!text) return text;

  let cleaned = text;

  // Remove numeric amendment marker prefixes while preserving wrapped content.
  // Example: "5[Provided that...]" -> "Provided that..."
  cleaned = cleaned.replace(/(\d+)\[([^\]]*)\]/g, '$2');

  // Remove empty marker artifacts.
  cleaned = cleaned.replace(/\[\*[\s\*]*\]/g, '');
  cleaned = cleaned.replace(/\[:\]/g, '');

  cleaned = stripFootnoteBlock(cleaned);
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  return cleaned;
}

function mapClause(node: AnyRecord, idx: number, parentId: string): BodyBlock {
  const label = trimOrEmpty(node.label);
  const id = trimOrEmpty(node.id) || `${parentId}-cl-${idx}`;
  const letter = stripParenLabel(label) || String(idx + 1);
  let text = cleanAmendmentMarkers(trimOrEmpty(node.text));
  const subClauses = Array.isArray(node.subClauses) ? (node.subClauses as AnyRecord[]) : [];
  if (subClauses.length) {
    const tail = subClauses
      .map((sc) => cleanAmendmentMarkers(trimOrEmpty(sc?.text)))
      .filter(Boolean)
      .join('\n');
    if (tail) text = text ? `${text}\n${tail}` : tail;
  }
  void id;
  return { kind: 'clause', letter, text };
}

function mapProviso(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = cleanAmendmentMarkers(node.trim());
    return t ? { kind: 'proviso', text: t } : null;
  }
  if (isObject(node)) {
    const lab = cleanAmendmentMarkers(trimOrEmpty(node.label));
    const text = cleanAmendmentMarkers(trimOrEmpty(node.text));
    const merged = [lab, text].filter(Boolean).join(' ').trim();
    return merged ? { kind: 'proviso', text: merged } : null;
  }
  return null;
}

function mapExplanation(node: unknown): BodyBlock | null {
  if (typeof node === 'string') {
    const t = cleanAmendmentMarkers(node.trim());
    return t ? { kind: 'explanation', text: t } : null;
  }
  if (isObject(node)) {
    const lab = cleanAmendmentMarkers(trimOrEmpty(node.label));
    const text = cleanAmendmentMarkers(trimOrEmpty(node.text));
    const merged = [lab, text].filter(Boolean).join(': ').trim();
    return merged ? { kind: 'explanation', text: merged } : null;
  }
  return null;
}

/**
 * Some family-suits sections embed the full clause run `(a)...(e)` directly
 * inside subsection text while also providing structured `clauses[]`.
 * When we keep both verbatim, the renderer shows a huge run-on paragraph
 * before the clause list. Trim subsection text to the lead-in before the
 * first clause marker so the UI renders as:
 *   (1) lead-in text
 *       (a) ...
 *       (b) ...
 */
function trimSubsectionLeadBeforeClauses(text: string, rawClauses: AnyRecord[]): string {
  if (!text || rawClauses.length === 0) return text;
  let cutAt = -1;
  for (const clause of rawClauses) {
    const marker = trimOrEmpty(clause.label);
    if (!marker) continue;
    const idx = text.indexOf(marker);
    if (idx >= 0 && (cutAt === -1 || idx < cutAt)) cutAt = idx;
  }
  if (cutAt <= 0) return text;
  const lead = text.slice(0, cutAt).trim();
  return lead || text;
}

function mapSubsection(node: AnyRecord, idx: number, parentId: string): BodyBlock {
  const label = trimOrEmpty(node.label);
  const number = stripParenLabel(label) || String(idx + 1);
  const textRaw = cleanAmendmentMarkers(trimOrEmpty(node.text));

  const children: BodyBlock[] = [];
  const clauses = Array.isArray(node.clauses) ? (node.clauses as AnyRecord[]) : [];
  const text = trimSubsectionLeadBeforeClauses(textRaw, clauses);
  clauses.forEach((c, i) => children.push(mapClause(c, i, parentId)));
  const provisos = Array.isArray(node.provisos) ? node.provisos : [];
  provisos.forEach((p) => {
    const block = mapProviso(p);
    if (block) children.push(block);
  });
  const explanations = Array.isArray(node.explanations) ? node.explanations : [];
  explanations.forEach((e) => {
    const block = mapExplanation(e);
    if (block) children.push(block);
  });

  return {
    kind: 'subsection',
    number,
    text,
    ...(children.length ? { children } : {}),
  };
}

function mapUnitBody(node: AnyRecord, unitId: string): BodyBlock[] {
  const blocks: BodyBlock[] = [];

  const subsRaw = node.subsections ?? node.sub_sections ?? node.subSections;
  const subsections = Array.isArray(subsRaw) ? (subsRaw as AnyRecord[]) : [];
  const subsectionProvisoTexts = new Set<string>();

  if (subsections.length === 0) {
    const text =
      cleanAmendmentMarkers(trimOrEmpty(node.fullText)) ||
      cleanAmendmentMarkers(trimOrEmpty(node.text)) ||
      cleanAmendmentMarkers(trimOrEmpty(node.content));
    if (text) blocks.push({ kind: 'paragraph', text });
    // scheduleItem typically has only `title` — surfaced via Unit.title.
  } else {
    subsections.forEach((sub, i) => {
      const subsectionBlock = mapSubsection(sub, i, unitId);
      if (subsectionBlock.children?.length) {
        subsectionBlock.children.forEach((child) => {
          if (child.kind === 'proviso' && child.text) {
            subsectionProvisoTexts.add(normalizeTextForCompare(child.text));
          }
        });
      }
      blocks.push(subsectionBlock);
    });
  }

  const provisosTop = Array.isArray(node.provisos) ? node.provisos : [];
  provisosTop.forEach((p) => {
    const block = mapProviso(p);
    if (!block) return;
    const norm = normalizeTextForCompare(block.text);
    if (subsectionProvisoTexts.has(norm)) return;
    blocks.push(block);
  });

  const explanationsTop = Array.isArray(node.explanations) ? node.explanations : [];
  explanationsTop.forEach((e) => {
    const block = mapExplanation(e);
    if (block) blocks.push(block);
  });

  return blocks;
}

function structuralHeading(node: AnyRecord, fallback: string): string {
  const label = trimOrEmpty(node.label);
  const title = trimOrEmpty(node.title);
  if (label && title && label.toLowerCase() !== title.toLowerCase()) return `${label} ${title}`;
  if (label) return label;
  if (title) return title;
  return fallback;
}

function isLeafBearing(container: AnyRecord): boolean {
  const children = Array.isArray(container.children) ? (container.children as unknown[]) : [];
  return children.some((c) => isObject(c) && LEAF_TYPES.has(trimOrEmpty(c.type)));
}

function makeUnit(node: AnyRecord, fallbackOrder: number): Unit {
  const type = trimOrEmpty(node.type) || 'section';
  const kind = pickUnitKind(type);
  const id = trimOrEmpty(node.id) || `${type || 'leaf'}-${fallbackOrder}`;
  const labelRaw = trimOrEmpty(node.label);
  const titleRaw = trimOrEmpty(node.title);
  const number = unitNumberFromNode(node, String(fallbackOrder));
  const title = titleRaw || labelRaw || `${kind[0].toUpperCase()}${kind.slice(1)} ${number}`;
  const numberDisplay = unitNumberDisplay(kind, number, labelRaw);
  const body = mapUnitBody(node, id);
  return {
    id,
    kind,
    number,
    numberDisplay,
    title,
    body,
  };
}

function buildPreamble(node: AnyRecord): PreambleNode | undefined {
  const id = trimOrEmpty(node.id) || 'preamble';
  const titleField = trimOrEmpty(node.title) || trimOrEmpty(node.label) || 'Preamble';

  const body: BodyBlock[] = [];

  const directText = trimOrEmpty(node.text);
  if (directText) body.push({ kind: 'paragraph', text: directText });

  const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
  children.forEach((child) => {
    if (!isObject(child)) return;
    body.push(...mapUnitBody(child, id));
  });

  if (body.length === 0) {
    body.push({ kind: 'paragraph', text: titleField });
  }

  return { id, title: titleField, body };
}

function buildStatute(raw: AnyRecord, hint?: string): Statute {
  const metadata = isObject(raw.metadata) ? (raw.metadata as AnyRecord) : {};
  const title = trimOrEmpty(metadata.title) || 'Untitled Statute';
  const shortCode = trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.shortCode) || title;
  const yearRaw = metadata.year;
  const year = typeof yearRaw === 'number' && !Number.isNaN(yearRaw) ? yearRaw : 0;
  const jurisdiction = trimOrEmpty(metadata.jurisdiction) || 'PK';
  const category = hint || trimOrEmpty(metadata.category) || 'family-suits';

  const structure = Array.isArray(raw.structure) ? (raw.structure as AnyRecord[]) : [];
  const groups: Group[] = [];
  let preamble: PreambleNode | undefined;
  let groupSeq = 0;
  let unitOrderSeq = 0;

  /** Append unit to a "synthetic" group by ancestor signature. Family-suits
   * acts often have all sections at the top level (no PRELIMINARY container);
   * we collapse them into one "Sections" group rather than creating a group
   * per stray section. */
  const appendStrayLeaf = (u: Unit, ancestors: string[]) => {
    const sig = ancestors.join('|');
    const prev = groups[groups.length - 1];
    const prevSig = prev?.ancestors?.join('|') ?? '';
    if (prev && prev.kind === 'other' && prevSig === sig) {
      prev.units.push(u);
      return;
    }
    groups.push({
      id: `grp-other-${groupSeq++}`,
      kind: 'other',
      label: ancestors[ancestors.length - 1] || 'Sections',
      units: [u],
      ...(ancestors.length ? { ancestors: ancestors.slice(0, -1) } : {}),
    });
  };

  const visit = (node: AnyRecord, ancestors: string[]) => {
    if (!isObject(node)) return;
    const type = trimOrEmpty(node.type);

    if (type === 'preamble') {
      if (!preamble) preamble = buildPreamble(node);
      return;
    }

    if (LEAF_TYPES.has(type)) {
      const u = makeUnit(node, unitOrderSeq++);
      appendStrayLeaf(u, ancestors);
      return;
    }

    if (CONTAINER_TYPES.has(type)) {
      const groupKind = pickGroupKind(type);
      const label = trimOrEmpty(node.label) || (type ? type[0].toUpperCase() + type.slice(1) : 'Section');
      const titleField = trimOrEmpty(node.title);
      const groupAncestors = ancestors.slice();

      if (isLeafBearing(node)) {
        const units: Unit[] = [];
        const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
        children.forEach((child) => {
          if (!isObject(child)) return;
          if (LEAF_TYPES.has(trimOrEmpty(child.type))) {
            units.push(makeUnit(child, unitOrderSeq++));
          }
        });

        if (units.length > 0) {
          const id = trimOrEmpty(node.id) || `grp-${type}-${groupSeq++}`;
          groups.push({
            id,
            kind: groupKind,
            label,
            ...(titleField ? { title: titleField } : {}),
            ...(groupAncestors.length ? { ancestors: groupAncestors } : {}),
            units,
          });
        }

        const trail = [...ancestors, structuralHeading(node, label)];
        children.forEach((child) => {
          if (!isObject(child)) return;
          if (CONTAINER_TYPES.has(trimOrEmpty(child.type))) visit(child, trail);
        });
        return;
      }

      const trail = [...ancestors, structuralHeading(node, label)];
      const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
      children.forEach((c) => visit(c as AnyRecord, trail));
      return;
    }

    if (type) {
      devWarn(`Unrecognized type "${type}" — descending into children`, node);
    }
    const children = Array.isArray(node.children) ? (node.children as AnyRecord[]) : [];
    children.forEach((c) => visit(c as AnyRecord, ancestors));
  };

  structure.forEach((node) => visit(node, []));

  const idHint = hint || trimOrEmpty(metadata.shortTitle) || trimOrEmpty(metadata.title) || 'statute';
  const id = idHint.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'statute';

  return {
    id,
    title,
    shortCode,
    year,
    jurisdiction,
    category,
    ...(preamble ? { preamble } : {}),
    groups,
    metadata,
  };
}

export function adaptStatute(rawJson: unknown, hint?: string): Statute {
  if (!isObject(rawJson)) {
    devWarn('adaptStatute received non-object input', rawJson);
    return {
      id: 'unknown',
      title: '',
      shortCode: '',
      year: 0,
      jurisdiction: 'PK',
      category: hint || 'family-suits',
      groups: [],
      metadata: {},
    };
  }
  return buildStatute(rawJson, hint);
}
